import AddLocationOutlinedIcon from '@mui/icons-material/AddLocationOutlined';
import {
	FormControl,
	Grid,
	InputAdornment,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	SelectChangeEvent,
	Typography,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import FindAnotherOffice from './FindAnotherOffice';

import DistanceIndicator from '@src/components/DateAndTime/Location/DistanceIndicator';
import { Application, OfficeSearchApiResponse } from '@src/lib/__generated/api';
import { useGetFacilityLocations } from '@src/services/useGetFacilityLocations';
import { useFacilityStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';

export default function LocationSelect() {
	const [openDrawer, setOpenDrawer] = useState(false);
	const facility = useFacilityStore((store) => store.facility);
	const setFacility = useFacilityStore((store) => store.setFacility);

	const digitalVisitReason = useReasonForVisitStore((store) => store.reasonForVisit);
	const longitude = usePatientInformationFormStore((store) => store.geoLocation.longitude);
	const latitude = usePatientInformationFormStore((store) => store.geoLocation.latitude);
	const patientAge = usePatientInformationFormStore((store) => store.patientAge());
	const { t } = useTranslation('dateAndTime');

	const facilityParams = {
		digitalVisitReason,
		longitude,
		latitude,
		application: Application.AspenDental,
		...(patientAge && { patientAge }),
	};

	const { data: facilityLocations } = useGetFacilityLocations(facilityParams);

	useEffect(() => {
		if (facilityLocations) {
			setFacility(facilityLocations[0]);
		}
	}, [facilityLocations, setFacility]);

	const handleChange = (event: SelectChangeEvent) => {
		if (event.target?.value) {
			setFacility(
				facilityLocations?.find(
					(location) => location.facilityCode === event.target.value
				) as OfficeSearchApiResponse
			);
		} else {
			setOpenDrawer(true);
		}
	};
	return (
		<FormControl fullWidth>
			<InputLabel id="location-label">{t('location')}</InputLabel>
			<Select
				labelId="location-label"
				startAdornment={
					<InputAdornment position="start">
						<AddLocationOutlinedIcon />
					</InputAdornment>
				}
				value={facility?.facilityCode || ''}
				onChange={handleChange}
				renderValue={(value) => facilityLocations?.find((location) => location.facilityCode === value)?.title}
				label="Location"
			>
				{facilityLocations?.slice(0, 5).map((location) => {
					return (
						<MenuItem
							key={location.facilityCode}
							value={location.facilityCode ?? ''}
							sx={{ lineHeight: 'normal', py: 2, px: 4 }}
						>
							<ListItemText
								disableTypography
								primary={
									<Grid container justifyContent="space-between">
										<Typography variant="bodySmallBook">{location.title}</Typography>
										<DistanceIndicator distance={location.distance} variant="bodySmallBook" />
									</Grid>
								}
								secondary={
									<Grid container>
										<Grid item xs={12}>
											<Typography variant="bodySmallBook">{location.address?.line1}</Typography>
										</Grid>
										<Grid item xs={12}>
											<Typography variant="bodySmallBook">{t('alignersAvailable')}</Typography>
										</Grid>
									</Grid>
								}
							/>
						</MenuItem>
					);
				})}
				<MenuItem sx={{ display: 'flex', justifyContent: 'center' }}>
					<Typography color="secondary" variant="buttonLabel">
						{t('findAnotherOffice')}
					</Typography>
				</MenuItem>
			</Select>
			<FindAnotherOffice
				openDrawer={openDrawer}
				setOpenDrawer={setOpenDrawer}
				facilityLocations={facilityLocations}
				setFacility={setFacility}
			/>
		</FormControl>
	);
}
